/* SQUISH / Dawn 15.4.1 cart drawer: resilient section rendering.
   Install alongside the matching assets/cart.js. */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.squishRenderVersion = 0;
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
  }

  setHeaderCartIconAccessibility() {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', (event) => { event.preventDefault(); this.open(link); });
    link.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') { event.preventDefault(); this.open(link); }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    requestAnimationFrame(() => this.classList.add('animate', 'active'));
    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty') : this.querySelector('#CartDrawer');
      const focus = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container && focus && typeof trapFocus === 'function') trapFocus(container, focus);
    }, { once: true });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', 'false');
    if (note.nextElementSibling?.id) note.setAttribute('aria-controls', note.nextElementSibling.id);
    note.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    note.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  async getFreshSections() {
    const root = window.Shopify?.routes?.root || '/';
    const path = root.endsWith('/') ? root : root + '/';
    const response = await fetch(`${path}?sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Section fetch HTTP ${response.status}`);
    const result = await response.json();
    if (!result || typeof result['cart-drawer'] !== 'string' || !result['cart-drawer']) {
      throw new Error('No cart-drawer HTML in fresh sections');
    }
    return result;
  }

  /* For cart.js quantity changes and add-to-cart rendering. Applies a complete
     drawer snapshot including the footer, rather than replacing only cart items. */
  async renderSections(sections, { open = false, forceFresh = false } = {}) {
    const version = ++this.squishRenderVersion;
    try {
      let sourceSections = sections;
      if (forceFresh || !sourceSections || typeof sourceSections['cart-drawer'] !== 'string' ||
          typeof sourceSections['cart-icon-bubble'] !== 'string') {
        sourceSections = await this.getFreshSections();
      }
      if (version !== this.squishRenderVersion) return false;
      const parsedDrawer = new DOMParser().parseFromString(sourceSections['cart-drawer'], 'text/html');
      const newBody = parsedDrawer.querySelector('#CartDrawer');
      const newRoot = parsedDrawer.querySelector('cart-drawer');
      const iconSource = new DOMParser().parseFromString(sourceSections['cart-icon-bubble'], 'text/html')
        .querySelector('#shopify-section-cart-icon-bubble');
      const currentBody = this.querySelector('#CartDrawer');
      const iconTarget = document.querySelector('#shopify-section-cart-icon-bubble');
      if (!newBody || !currentBody) throw new Error('Cart drawer markup is incomplete');
      if (version !== this.squishRenderVersion) return false;
      currentBody.innerHTML = newBody.innerHTML;
      const empty = newRoot?.classList.contains('is-empty') ?? false;
      this.classList.toggle('is-empty', empty);
      if (iconSource && iconTarget) iconTarget.innerHTML = iconSource.innerHTML;
      this.bindOverlay();
      if (open) this.open();
      return true;
    } catch (error) {
      console.error('[SQUISH] Cart drawer section refresh failed', error);
      return false;
    }
  }

  async renderContents(state) {
    this.productId = state?.id;
    return this.renderSections(state?.sections, { open: true });
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  setActiveElement(element) { this.activeElement = element; }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}
if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);